import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ExternalLink,
  User,
  Scale,
  Calendar,
  CheckSquare,
  DollarSign,
  FileText,
  Loader2,
  AlertCircle,
  Mail,
  MessageSquare,
  Briefcase,
  Clock,
  Receipt,
  Plus,
  Upload,
  AlertTriangle,
  Info,
  CircleAlert,
  Check,
  CreditCard,
} from "lucide-react";
import { mattersApi } from "../../services/api";
import { useMatterInspector } from "../../contexts/MatterInspectorContext";
import TimerSubPanel from "./TimerSubPanel";
import InvoiceSubPanel from "./InvoiceSubPanel";
import TaskSubPanel from "./TaskSubPanel";

// ── Helpers ──────────────────────────────────────────────────────────

/** Convert snake_case string to Title Case */
const toTitleCase = (s: string) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** Format cents to dollar string with commas */
const formatCurrency = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;

/** Format a date string to "MMM DD, YYYY" */
const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/** Relative time string (e.g. "2h ago", "3d ago") */
const relativeTime = (dateStr: string) => {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}mo ago`;
};

// ── Color Maps ───────────────────────────────────────────────────────

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  open: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Active" },
  closed: { bg: "bg-slate-100", text: "text-slate-600", label: "Closed" },
  on_hold: { bg: "bg-amber-100", text: "text-amber-700", label: "On Hold" },
};

const practiceAreaColors: Record<string, string> = {
  real_estate: "bg-cyan-100 text-cyan-700",
  immigration: "bg-emerald-100 text-emerald-700",
  family: "bg-pink-100 text-pink-700",
  criminal: "bg-red-100 text-red-700",
  corporate: "bg-purple-100 text-purple-700",
  litigation: "bg-blue-100 text-blue-700",
  wills_estates: "bg-indigo-100 text-indigo-700",
  personal_injury: "bg-orange-100 text-orange-700",
  employment_labour: "bg-amber-100 text-amber-700",
  tax: "bg-rose-100 text-rose-700",
  intellectual_property: "bg-violet-100 text-violet-700",
  environmental: "bg-teal-100 text-teal-700",
  administrative: "bg-sky-100 text-sky-700",
  bankruptcy: "bg-red-100 text-red-600",
  other: "bg-slate-100 text-slate-600",
};

const importantDateKindColors: Record<string, string> = {
  deadline: "bg-red-100 text-red-700",
  hearing: "bg-blue-100 text-blue-700",
  closing: "bg-cyan-100 text-cyan-700",
  filing: "bg-amber-100 text-amber-700",
  meeting: "bg-purple-100 text-purple-700",
  other: "bg-slate-100 text-slate-600",
};

// ── Component ────────────────────────────────────────────────────────

type SubPanel = "timer" | "invoice" | "task" | null;

const MatterInspectorDrawer: React.FC = () => {
  const { isOpen, activeMatterId, closeMatterInspector } = useMatterInspector();
  const navigate = useNavigate();

  const [matter, setMatter] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changingTo, setChangingTo] = useState<string | null>(null);
  const [activeSubPanel, setActiveSubPanel] = useState<SubPanel>(null);
  const [actionFeedback, setActionFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // ── Data Fetching ──────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen || !activeMatterId) return;
    setLoading(true);
    setError(null);
    setActiveSubPanel(null);
    Promise.all([
      mattersApi.get(activeMatterId),
      mattersApi.getSummary(activeMatterId),
    ])
      .then(([matterRes, summaryRes]) => {
        setMatter(matterRes.data);
        setSummary(summaryRes.data);
      })
      .catch(() => setError("Failed to load matter details"))
      .finally(() => setLoading(false));
  }, [isOpen, activeMatterId]);

  // Reset sub-panel when drawer closes
  useEffect(() => {
    if (!isOpen) setActiveSubPanel(null);
  }, [isOpen]);

  // ── Auto-dismiss feedback ─────────────────────────────────────────

  useEffect(() => {
    if (!actionFeedback) return;
    const t = setTimeout(() => setActionFeedback(null), 3000);
    return () => clearTimeout(t);
  }, [actionFeedback]);

  // ── ESC Key Handler (sub-panel first, then drawer) ────────────────

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (activeSubPanel) {
          setActiveSubPanel(null);
        } else {
          closeMatterInspector();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, activeSubPanel, closeMatterInspector]);

  // ── Navigation helpers ─────────────────────────────────────────────

  const openFullMatter = () => {
    navigate(`/matters/${activeMatterId}`);
    closeMatterInspector();
  };

  const openClient = (clientId: string) => {
    navigate(`/clients/${clientId}`);
    closeMatterInspector();
  };

  // ── Quick Action Handlers ───────────────────────────────────────────

  const handleStatusChange = async (newStatus: string) => {
    if (!activeMatterId || changingTo || matter?.status === newStatus) return;
    setChangingTo(newStatus);
    try {
      await mattersApi.changeStatus(activeMatterId, newStatus);
      setMatter((prev: any) => ({ ...prev, status: newStatus }));
      const label = statusColors[newStatus]?.label || newStatus;
      setActionFeedback({ type: "success", message: `Status changed to ${label}` });
    } catch {
      setActionFeedback({ type: "error", message: "Failed to change status" });
    } finally {
      setChangingTo(null);
    }
  };

  const handleNavigateToTab = (tab: string, action?: string) => {
    const url = action
      ? `/matters/${activeMatterId}?tab=${tab}&action=${action}`
      : `/matters/${activeMatterId}?tab=${tab}`;
    navigate(url);
    closeMatterInspector();
  };

  // ── Derived data ───────────────────────────────────────────────────

  const practiceAreas: string[] = matter?.practiceAreas || (matter?.practiceArea ? [matter.practiceArea] : []);

  const upcomingDates = (() => {
    if (!matter?.importantDates?.length) return [];
    const now = new Date();
    return matter.importantDates
      .filter((d: any) => new Date(d.at) >= now)
      .sort((a: any, b: any) => new Date(a.at).getTime() - new Date(b.at).getTime())
      .slice(0, 3);
  })();

  const hasReferences =
    matter?.references?.externalFileNumber ||
    matter?.references?.governmentRef ||
    matter?.references?.property?.addressText;

  const resolvedClientId = matter
    ? typeof matter.clientId === "object" ? matter.clientId._id : matter.clientId
    : "";
  const resolvedClientName = matter && typeof matter.clientId === "object"
    ? (matter.clientId.displayName || matter.clientId.name)
    : undefined;

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="matter-inspector-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 z-[70]"
            onClick={() => {
              setActiveSubPanel(null);
              closeMatterInspector();
            }}
          />

          {/* Drawer */}
          <motion.div
            key="matter-inspector-drawer"
            initial={{ x: 560, opacity: 0 }}
            animate={{
              x: 0,
              opacity: 1,
              width: activeSubPanel ? 960 : 540,
            }}
            exit={{ x: 1000, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed top-0 right-0 h-screen max-md:!w-full max-w-[100vw] bg-white border-l border-slate-200 shadow-2xl z-[71] flex flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Left Column: Matter Details ────────────────────────── */}
            <div
              className={`flex flex-col h-full shrink-0 overflow-hidden ${
                activeSubPanel
                  ? "w-0 md:w-[420px] max-md:hidden"
                  : "w-full"
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white shrink-0">
                <button
                  onClick={closeMatterInspector}
                  className="text-slate-400 hover:text-slate-800 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
                <button
                  onClick={openFullMatter}
                  className="flex items-center gap-1.5 text-sm font-medium text-white rounded-lg px-3 py-1.5 transition-colors"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
                >
                  Open Full Matter View
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Quick Actions Bar */}
              {!loading && !error && matter && (
                <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 shrink-0 space-y-2">
                  {/* Action Feedback Toast */}
                  <AnimatePresence>
                    {actionFeedback && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
                          actionFeedback.type === "success"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-red-50 text-red-700 border border-red-200"
                        }`}
                      >
                        {actionFeedback.type === "success" ? (
                          <Check className="h-3.5 w-3.5 shrink-0" />
                        ) : (
                          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        )}
                        {actionFeedback.message}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => setActiveSubPanel(activeSubPanel === "timer" ? null : "timer")}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
                        activeSubPanel === "timer"
                          ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                          : "border-slate-200 text-slate-600 bg-white hover:bg-slate-100"
                      }`}
                    >
                      <Clock className="h-3.5 w-3.5" />
                      Timer
                    </button>

                    <button
                      onClick={() => setActiveSubPanel(activeSubPanel === "task" ? null : "task")}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
                        activeSubPanel === "task"
                          ? "border-purple-300 bg-purple-50 text-purple-700"
                          : "border-slate-200 text-slate-600 bg-white hover:bg-slate-100"
                      }`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Task
                    </button>

                    <button
                      onClick={() => setActiveSubPanel(activeSubPanel === "invoice" ? null : "invoice")}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
                        activeSubPanel === "invoice"
                          ? "border-blue-300 bg-blue-50 text-blue-700"
                          : "border-slate-200 text-slate-600 bg-white hover:bg-slate-100"
                      }`}
                    >
                      <Receipt className="h-3.5 w-3.5" />
                      Invoice
                    </button>
                  </div>
                </div>
              )}

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
                {/* Loading State */}
                {loading && (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    <p className="text-sm text-slate-500">Loading matter details...</p>
                  </div>
                )}

                {/* Error State */}
                {error && !loading && (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <AlertCircle className="h-6 w-6 text-red-400" />
                    <p className="text-sm text-red-600">{error}</p>
                    <button
                      onClick={() => {
                        if (activeMatterId) {
                          setLoading(true);
                          setError(null);
                          Promise.all([
                            mattersApi.get(activeMatterId),
                            mattersApi.getSummary(activeMatterId),
                          ])
                            .then(([matterRes, summaryRes]) => {
                              setMatter(matterRes.data);
                              setSummary(summaryRes.data);
                            })
                            .catch(() => setError("Failed to load matter details"))
                            .finally(() => setLoading(false));
                        }
                      }}
                      className="text-sm font-medium text-white rounded-lg px-4 py-2 transition-colors"
                      style={{ backgroundColor: "var(--color-primary)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
                    >
                      Retry
                    </button>
                  </div>
                )}

                {/* Content */}
                {!loading && !error && matter && summary && (
                  <>
                    {/* ── Title Block + Status Selector ─────────────── */}
                    <div>
                      {matter.matterNumber && (
                        <p className="text-xs font-mono text-slate-400 mb-1">{matter.matterNumber}</p>
                      )}
                      <h2 className="text-lg font-bold text-slate-800 leading-tight">{matter.title}</h2>
                      <div className="flex items-center gap-1.5 mt-2.5">
                        {[
                          { value: "open", label: "Active", dot: "bg-emerald-500", selectedBg: "bg-emerald-50", selectedBorder: "border-emerald-200", selectedText: "text-emerald-700" },
                          { value: "on_hold", label: "On Hold", dot: "bg-amber-500", selectedBg: "bg-amber-50", selectedBorder: "border-amber-200", selectedText: "text-amber-700" },
                          { value: "closed", label: "Closed", dot: "bg-slate-400", selectedBg: "bg-slate-100", selectedBorder: "border-slate-300", selectedText: "text-slate-600" },
                        ].map((s) => {
                          const isSelected = matter.status === s.value;
                          const isChanging = changingTo === s.value;
                          return (
                            <button
                              key={s.value}
                              onClick={() => handleStatusChange(s.value)}
                              disabled={!!changingTo}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors disabled:cursor-not-allowed ${
                                isSelected
                                  ? `${s.selectedBg} ${s.selectedBorder} ${s.selectedText}`
                                  : "bg-white border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                              } ${changingTo && !isChanging ? "opacity-40" : ""}`}
                            >
                              {isChanging ? (
                                <Loader2 className="h-2.5 w-2.5 animate-spin" />
                              ) : (
                                <span className={`h-2 w-2 rounded-full ${isSelected ? s.dot : "bg-slate-300"}`} />
                              )}
                              {s.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* ── Client ────────────────────────────────────── */}
                    {matter.clientId && (
                      <div className="border-t border-slate-100 pt-4">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                          <button
                            onClick={() => {
                              const clientId =
                                typeof matter.clientId === "object"
                                  ? matter.clientId._id
                                  : matter.clientId;
                              openClient(clientId);
                            }}
                            className="text-sm font-medium text-slate-700 hover:underline transition-colors"
                            style={{ color: "var(--color-accent)" }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-primary)")}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-accent)")}
                          >
                            {typeof matter.clientId === "object"
                              ? matter.clientId.displayName || matter.clientId.name || "Client"
                              : "View Client"}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* ── Team ──────────────────────────────────────── */}
                    <div>
                      {matter.responsibleLawyerId && (
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-slate-400" />
                          <span className="text-sm text-slate-700">
                            {typeof matter.responsibleLawyerId === "object"
                              ? matter.responsibleLawyerId.name
                              : "Responsible Lawyer"}
                          </span>
                          <span
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white"
                            style={{ backgroundColor: "var(--color-primary)" }}
                          >
                            Lead
                          </span>
                        </div>
                      )}
                      {matter.assignedUserIds?.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1 ml-6">
                          + {matter.assignedUserIds.length} assigned
                        </p>
                      )}
                    </div>

                    {/* ── Practice Areas ────────────────────────────── */}
                    {practiceAreas.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {practiceAreas.map((area: string) => (
                          <span
                            key={area}
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              practiceAreaColors[area] || "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {toTitleCase(area)}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* ── Summary Cards ─────────────────────────────── */}
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        onClick={() => setActiveSubPanel(activeSubPanel === "task" ? null : "task")}
                        className={`rounded-lg p-3 text-center cursor-pointer transition-colors ${
                          activeSubPanel === "task"
                            ? "bg-purple-50 ring-1 ring-purple-200"
                            : "bg-slate-50 hover:bg-slate-100"
                        }`}
                      >
                        <CheckSquare className={`h-4 w-4 mx-auto mb-1 ${activeSubPanel === "task" ? "text-purple-500" : "text-slate-400"}`} />
                        <p className="text-lg font-bold text-slate-800">
                          {summary.tasks?.openCount ?? 0}
                        </p>
                        <p className="text-[11px] text-slate-500">Open Tasks</p>
                        {summary.tasks?.overdueCount > 0 && (
                          <p className="text-[10px] text-red-600 font-medium mt-0.5">
                            {summary.tasks.overdueCount} overdue
                          </p>
                        )}
                      </button>

                      <button
                        onClick={() => setActiveSubPanel(activeSubPanel === "invoice" ? null : "invoice")}
                        className={`rounded-lg p-3 text-center cursor-pointer transition-colors ${
                          activeSubPanel === "invoice"
                            ? "bg-blue-50 ring-1 ring-blue-200"
                            : "bg-slate-50 hover:bg-slate-100"
                        }`}
                      >
                        <DollarSign className={`h-4 w-4 mx-auto mb-1 ${activeSubPanel === "invoice" ? "text-blue-500" : "text-slate-400"}`} />
                        <p className="text-lg font-bold text-slate-800">
                          {formatCurrency(summary.billing?.outstandingBalanceCents ?? 0)}
                        </p>
                        <p className="text-[11px] text-slate-500">Outstanding</p>
                        {summary.billing?.trustBalanceCents > 0 && (
                          <p className="text-[10px] text-emerald-600 font-medium mt-0.5">
                            Trust: {formatCurrency(summary.billing.trustBalanceCents)}
                          </p>
                        )}
                      </button>

                      <button
                        onClick={() => handleNavigateToTab("documents")}
                        className="bg-slate-50 rounded-lg p-3 text-center cursor-pointer hover:bg-slate-100 transition-colors"
                      >
                        <FileText className="h-4 w-4 text-slate-400 mx-auto mb-1" />
                        <p className="text-lg font-bold text-slate-800">
                          {summary.documents?.count ?? 0}
                        </p>
                        <p className="text-[11px] text-slate-500">Documents</p>
                      </button>
                    </div>

                    {/* ── Smart Insights ─────────────────────────────── */}
                    {(() => {
                      const insights: Array<{
                        key: string;
                        icon: React.ReactNode;
                        message: string;
                        bg: string;
                        textColor: string;
                        action?: { label: string; onClick: () => void; borderColor: string; textColor: string };
                      }> = [];

                      if (
                        summary.billing?.outstandingBalanceCents === 0 &&
                        summary.billing?.outstandingInvoiceCount === 0 &&
                        matter.status === "open"
                      ) {
                        insights.push({
                          key: "no-invoices",
                          icon: <Receipt className="h-4 w-4 text-amber-600 shrink-0" />,
                          message: "No invoices created yet",
                          bg: "bg-amber-50",
                          textColor: "text-amber-800",
                          action: {
                            label: "Create Invoice",
                            onClick: () => setActiveSubPanel("invoice"),
                            borderColor: "border-amber-300",
                            textColor: "text-amber-700",
                          },
                        });
                      }

                      if (summary.tasks?.overdueCount > 0) {
                        insights.push({
                          key: "overdue-tasks",
                          icon: <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />,
                          message: `${summary.tasks.overdueCount} overdue task${summary.tasks.overdueCount > 1 ? "s" : ""} need attention`,
                          bg: "bg-red-50",
                          textColor: "text-red-800",
                          action: {
                            label: "View Tasks",
                            onClick: () => setActiveSubPanel("task"),
                            borderColor: "border-red-300",
                            textColor: "text-red-700",
                          },
                        });
                      }

                      if (summary.billing?.trustBalanceCents < 0) {
                        insights.push({
                          key: "trust-deficit",
                          icon: <CircleAlert className="h-4 w-4 text-red-600 shrink-0" />,
                          message: `Trust account in deficit by ${formatCurrency(Math.abs(summary.billing.trustBalanceCents))}`,
                          bg: "bg-red-50",
                          textColor: "text-red-800",
                        });
                      }

                      if (summary.documents?.count === 0 && matter.status === "open") {
                        insights.push({
                          key: "no-documents",
                          icon: <Upload className="h-4 w-4 text-blue-600 shrink-0" />,
                          message: "No documents uploaded yet",
                          bg: "bg-blue-50",
                          textColor: "text-blue-800",
                          action: {
                            label: "Upload",
                            onClick: () => handleNavigateToTab("documents"),
                            borderColor: "border-blue-300",
                            textColor: "text-blue-700",
                          },
                        });
                      }

                      if (!matter.dueDate && matter.status === "open") {
                        insights.push({
                          key: "no-due-date",
                          icon: <Info className="h-4 w-4 text-slate-400 shrink-0" />,
                          message: "No due date set",
                          bg: "bg-slate-50",
                          textColor: "text-slate-600",
                        });
                      }

                      if (insights.length === 0) return null;

                      return (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-slate-700">Insights</p>
                          {insights.map((insight) => (
                            <div
                              key={insight.key}
                              className={`rounded-lg px-3 py-2.5 flex items-center gap-2.5 text-sm ${insight.bg}`}
                            >
                              {insight.icon}
                              <span className={`${insight.textColor} flex-1`}>{insight.message}</span>
                              {insight.action && (
                                <button
                                  onClick={insight.action.onClick}
                                  className={`text-xs font-medium px-2.5 py-1 rounded-md border ml-auto shrink-0 bg-white transition-colors hover:bg-slate-50 ${insight.action.borderColor} ${insight.action.textColor}`}
                                >
                                  {insight.action.label}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* ── Key Dates ─────────────────────────────────── */}
                    {(matter.dueDate || matter.openedAt || upcomingDates.length > 0) && (
                      <div className="border-t border-slate-100 pt-4 space-y-2">
                        <p className="text-sm font-semibold text-slate-700 mb-2">Key Dates</p>

                        {matter.dueDate && (
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            <span className="text-slate-500">Due Date:</span>
                            <span className="text-slate-700 font-medium">{formatDate(matter.dueDate)}</span>
                          </div>
                        )}

                        {matter.openedAt && (
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            <span className="text-slate-500">Opened:</span>
                            <span className="text-slate-700 font-medium">{formatDate(matter.openedAt)}</span>
                          </div>
                        )}

                        {upcomingDates.map((d: any, i: number) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <Clock className="h-4 w-4 text-slate-400" />
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                                importantDateKindColors[d.kind] || importantDateKindColors.other
                              }`}
                            >
                              {toTitleCase(d.kind || "other")}
                            </span>
                            <span className="text-slate-600 truncate">{d.label}</span>
                            <span className="text-slate-400 text-xs ml-auto shrink-0">{formatDate(d.at)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ── Billing Type ──────────────────────────────── */}
                    {matter.billing?.type && (
                      <div className="flex items-center gap-2 text-sm">
                        <Scale className="h-4 w-4 text-slate-400" />
                        <span className="text-slate-500">Billing:</span>
                        <span className="text-slate-700 font-medium">
                          {toTitleCase(matter.billing.type)}
                        </span>
                        {matter.billing.type === "flat" && matter.billing.flatFeeCents && (
                          <span className="text-slate-500 ml-1">
                            ({formatCurrency(matter.billing.flatFeeCents)})
                          </span>
                        )}
                      </div>
                    )}

                    {/* ── Payment Method ───────────────────────────── */}
                    <div className="flex items-center gap-2 text-sm">
                      <CreditCard className="h-4 w-4 text-slate-400" />
                      <span className="text-slate-500">Payment:</span>
                      {matter.billing?.preferredPaymentMethod ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          ({
                            credit_card: "bg-blue-50 text-blue-700",
                            etransfer: "bg-purple-50 text-purple-700",
                            wire: "bg-indigo-50 text-indigo-700",
                            cheque: "bg-amber-50 text-amber-700",
                            eft: "bg-cyan-50 text-cyan-700",
                            cash: "bg-emerald-50 text-emerald-700",
                          } as Record<string, string>)[matter.billing.preferredPaymentMethod] || "bg-slate-100 text-slate-600"
                        }`}>
                          {({
                            credit_card: "Credit Card",
                            etransfer: "e-Transfer",
                            wire: "Wire",
                            cheque: "Cheque",
                            eft: "EFT",
                            cash: "Cash",
                          } as Record<string, string>)[matter.billing.preferredPaymentMethod] || matter.billing.preferredPaymentMethod}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs italic">Not set</span>
                      )}
                    </div>

                    {/* ── Tags ──────────────────────────────────────── */}
                    {matter.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {matter.tags.map((tag: string, i: number) => (
                          <span
                            key={i}
                            className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* ── References ────────────────────────────────── */}
                    {hasReferences && (
                      <div className="border-t border-slate-100 pt-4 space-y-2">
                        <p className="text-sm font-semibold text-slate-700 mb-2">References</p>
                        {matter.references.externalFileNumber && (
                          <div className="text-sm">
                            <span className="text-slate-500">External File #: </span>
                            <span className="text-slate-700">{matter.references.externalFileNumber}</span>
                          </div>
                        )}
                        {matter.references.governmentRef && (
                          <div className="text-sm">
                            <span className="text-slate-500">Government Ref: </span>
                            <span className="text-slate-700">{matter.references.governmentRef}</span>
                          </div>
                        )}
                        {matter.references.property?.addressText && (
                          <div className="text-sm">
                            <span className="text-slate-500">Property Address: </span>
                            <span className="text-slate-700">{matter.references.property.addressText}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Recent Conversations ──────────────────────── */}
                    {summary.conversations?.length > 0 && (
                      <div className="border-t border-slate-100 pt-4">
                        <p className="text-sm font-semibold text-slate-700 mb-3">Recent Conversations</p>
                        <div className="space-y-2">
                          {summary.conversations.slice(0, 5).map((conv: any, i: number) => (
                            <div key={i} className="flex items-start gap-2">
                              {conv.channels?.primaryChannel === "email" ? (
                                <Mail className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                              ) : (
                                <MessageSquare className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-slate-700 truncate">
                                  {conv.subject || conv.preview || "No subject"}
                                </p>
                              </div>
                              <span className="text-[11px] text-slate-400 shrink-0 mt-0.5">
                                {conv.lastMessageAt ? relativeTime(conv.lastMessageAt) : ""}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── Right Column: Sub-Panel ────────────────────────────── */}
            <AnimatePresence>
              {activeSubPanel && matter && activeMatterId && (
                <motion.div
                  key={`sub-panel-${activeSubPanel}`}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 40 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="flex-1 border-l border-slate-200 max-md:border-l-0 flex flex-col h-full min-w-0 bg-white"
                >
                  {activeSubPanel === "timer" && (
                    <TimerSubPanel
                      matterId={activeMatterId}
                      clientId={resolvedClientId}
                      matterTitle={matter.title}
                      matterNumber={matter.matterNumber}
                      onStarted={() =>
                        setActionFeedback({ type: "success", message: "Timer started" })
                      }
                      onClose={() => setActiveSubPanel(null)}
                    />
                  )}
                  {activeSubPanel === "invoice" && (
                    <InvoiceSubPanel
                      matterId={activeMatterId}
                      clientId={resolvedClientId}
                      clientName={resolvedClientName}
                      matterTitle={matter.title}
                      matterNumber={matter.matterNumber}
                      onCreated={(invoiceId) => {
                        setActionFeedback({ type: "success", message: "Invoice created & sent" });
                        navigate(`/billing?invoice=${invoiceId}`);
                        closeMatterInspector();
                      }}
                      onClose={() => setActiveSubPanel(null)}
                    />
                  )}
                  {activeSubPanel === "task" && (
                    <TaskSubPanel
                      matterId={activeMatterId}
                      clientId={resolvedClientId}
                      matterTitle={matter.title}
                      matterNumber={matter.matterNumber}
                      upcomingTasks={summary?.tasks?.upcoming}
                      openCount={summary?.tasks?.openCount}
                      overdueCount={summary?.tasks?.overdueCount}
                      onCreated={() =>
                        setActionFeedback({ type: "success", message: "Task created" })
                      }
                      onClose={() => setActiveSubPanel(null)}
                      onViewAll={() => handleNavigateToTab("tasks")}
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MatterInspectorDrawer;
